import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './i18n';
import './styles/app.css';
import { App } from './App';
import { AppProvider } from './lib/AppContext';
import { applyBrand, fetchVmsBranding, loadAppConfig, withVmsBranding } from './lib/appConfig';
import { fetchKinioFamily, withKinioFamily } from './lib/kinioFamily';
import { initSession, restore, trackActivity } from './lib/session';
import { installApiErrorLog } from './lib/supportApi';
import { multiScreen } from './lib/multiScreen';
import { BootSkeleton } from './components/Skeleton';

const root = createRoot(document.getElementById('root')!);

async function boot() {
  // 設定、品牌、登入狀態問回來前（最多約 2 秒）先畫殼層骨架，不要整頁黑（B-29）
  root.render(<BootSkeleton />);
  const loaded = await loadAppConfig();
  initSession(loaded);
  installApiErrorLog(); // 問題回報的 context.json 帶最近 20 筆 API 錯誤（SPEC #support-business）
  trackActivity();
  multiScreen.start(); // LIVE-10：第一次畫面前就開始握手（握手期間不轉即時、不彈警報）
  // 品牌的 VMS 圖示跟恢復 session 一起問（最多 2 秒）；問完才套 favicon、畫 logo，不先閃 brand pack 的圖（SPEC #catalog-s17-10）
  // Kinio Station / Node 開放的品牌也一起問（最多 2 秒，失敗用預設值；SPEC #catalog-kinio-family-brands）
  const [branding, family] = await Promise.all([
    fetchVmsBranding(loaded),
    fetchKinioFamily(loaded),
    restore().catch(() => null), // cookie 沒了 / 失效 → 登入頁
  ]);
  const config = withKinioFamily(withVmsBranding(loaded, branding), family);
  applyBrand(config);
  const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });
  root.render(
    <StrictMode>
      <AppProvider config={config}>
        <QueryClientProvider client={qc}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </AppProvider>
    </StrictMode>,
  );
}

boot().catch((e) => {
  root.render(<pre style={{ padding: 24, color: '#f85149' }}>VMS 啟動失敗：{String(e?.message ?? e)}</pre>);
});
